import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { PHONE_NUMBER } from "@src/constants";
import { ApplicationProvider } from "@src/context/ApplicationContext";
import { CanvasProvider } from "@src/context/CanvasContext";
import { ComponentNameProvider } from "@src/context/ComponentNameContext";
import { EnvironmentProvider, useEnvironment } from "@src/context/EnvironmentContext";
import { FeatureFlagProvider } from "@src/context/FeatureFlagContext";
import { FeatureManifestProvider } from "@src/context/FeatureManifestContext";
import { InteractionGuardProvider } from "@src/context/InteractionGuardContext";
import {
  getStoredBranding,
  syncStoredBrandingWithUtmTheme,
  THEME_STORAGE_KEY,
  ThemeContextProvider,
} from "@src/context/ThemeContext";
import { TimerProvider } from "@src/context/TimerContext";
import { UTMProvider, useUTMContext } from "@src/context/UTMContext";
import { useInvocaPhoneNumberTracking } from "@src/hooks/useInvocaPhoneNumberTracking";
import type { AppThemeConfig } from "@src/interfaces/theme-config.interface";
import { Routes } from "@src/routes";
import { CanvasPrefillController } from "@src/services/canvas/prefill";
import { fetchPartners } from "@src/services/partners.service";
import { getThemeNameFromTag, OPTUM_THEME_NAME, THEME_CONFIGS } from "@src/themes";
import * as CreateRouterModule from "@uhg-abyss/web/tools/createRouter";
import * as ThemeModule from "@uhg-abyss/web/tools/theme";
import * as AbyssProviderModule from "@uhg-abyss/web/ui/AbyssProvider";
import * as RouterProviderModule from "@uhg-abyss/web/ui/RouterProvider";
import * as ThemeProviderModule from "@uhg-abyss/web/ui/ThemeProvider";
import type { ComponentType, FC, ReactElement, ReactNode } from "react";
import { useEffect, useState } from "react";

type CreateRouterFn = (routes: ReactElement | (() => ReactElement)) => unknown;
type ThemeConfig = Pick<AppThemeConfig, "theme">;
type CreateThemeFn = (brand: string, config?: ThemeConfig) => unknown;
type ProviderComponent = ComponentType<{
  children?: ReactNode;
  router?: unknown;
  theme?: unknown;
}>;

const createRouter = (CreateRouterModule as { createRouter?: CreateRouterFn }).createRouter;
const createTheme = (ThemeModule as { createTheme?: CreateThemeFn }).createTheme;

const AbyssProvider = (AbyssProviderModule as { AbyssProvider?: ProviderComponent }).AbyssProvider;
const ThemeProvider = (ThemeProviderModule as { ThemeProvider?: ProviderComponent }).ThemeProvider;
const RouterProvider = (RouterProviderModule as { RouterProvider?: ProviderComponent })
  .RouterProvider;

/**
 * The manifest provider must be part of the routed tree so its loading fallback
 * renders under AbyssProvider's theme context. Rendering it around AbyssProvider
 * leaves LoadingSpinner without the `sizes` token collection.
 */
const FeatureGatedRoutes = (): ReactElement => (
  <FeatureManifestProvider>
    <Routes />
  </FeatureManifestProvider>
);

const router = createRouter?.(FeatureGatedRoutes);

/**
 * Resolves the active branding tag to use for theme creation.
 *
 * Priority:
 * 1. utm_theme from UTMContext when present
 * 2. Runtime branding set by the Refer flow (persisted to localStorage via ThemeContext)
 *
 * Lives in browser.tsx (above the provider tree) because createTheme must be called
 * before ThemeContextProvider renders. ThemeContext owns the write side (setBranding,
 * clearBranding, event dispatch); this hook owns the read side at boot time.
 */
const useActiveBrandingTag = (utmThemeTag?: string): string | undefined => {
  const [runtimeBrandingTag, setRuntimeBrandingTag] = useState<string | undefined>(
    getStoredBranding,
  );

  useEffect(() => {
    const syncedBranding = syncStoredBrandingWithUtmTheme(utmThemeTag);
    if (syncedBranding) {
      setRuntimeBrandingTag(syncedBranding);
    }
  }, [utmThemeTag]);

  useEffect(() => {
    if (utmThemeTag) {
      return;
    }

    const handleBrandingChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ branding?: string }>;
      setRuntimeBrandingTag(customEvent.detail?.branding);
    };

    window.addEventListener("brandingchange", handleBrandingChange as EventListener);

    return () => {
      window.removeEventListener("brandingchange", handleBrandingChange as EventListener);
    };
  }, [utmThemeTag]);

  return utmThemeTag ?? runtimeBrandingTag;
};

const getParentOrganizationFromReferPath = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const referSegmentIndex = pathSegments.findIndex((pathSegment) => {
    return pathSegment.toLowerCase() === "refer";
  });

  if (referSegmentIndex < 0 || referSegmentIndex + 1 >= pathSegments.length) {
    return null;
  }

  try {
    return decodeURIComponent(pathSegments[referSegmentIndex + 1]);
  } catch {
    return null;
  }
};

const setBootstrappedBranding = (brandingTag: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedBranding = getThemeNameFromTag(brandingTag);
  localStorage.setItem(THEME_STORAGE_KEY, normalizedBranding);
  window.dispatchEvent(
    new CustomEvent("brandingchange", {
      detail: { branding: normalizedBranding },
    }),
  );
};

const useReferBrandingBootstrap = (utmThemeTag?: string) => {
  const [bootstrappedBrandingTag, setBootstrappedBrandingTag] = useState<string | undefined>();
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return Boolean(!utmThemeTag && getParentOrganizationFromReferPath());
  });

  useEffect(() => {
    if (utmThemeTag) {
      setIsBootstrapping(false);
      return;
    }

    const parentOrganization = getParentOrganizationFromReferPath();
    if (!parentOrganization) {
      setIsBootstrapping(false);
      return;
    }

    let isCancelled = false;

    const bootstrapReferBranding = async () => {
      try {
        const partnersResponse = await fetchPartners(parentOrganization);
        const partners = Array.isArray(partnersResponse) ? partnersResponse : [];

        const resolvedBrandingTag =
          partners.length === 1 ? getThemeNameFromTag(partners[0]?.branding) : OPTUM_THEME_NAME;

        if (!isCancelled) {
          setBootstrappedBrandingTag(resolvedBrandingTag);
          setBootstrappedBranding(resolvedBrandingTag);
        }
      } catch {
        if (!isCancelled) {
          setBootstrappedBrandingTag(OPTUM_THEME_NAME);
          setBootstrappedBranding(OPTUM_THEME_NAME);
        }
      } finally {
        if (!isCancelled) {
          setIsBootstrapping(false);
        }
      }
    };

    bootstrapReferBranding();

    return () => {
      isCancelled = true;
    };
  }, [utmThemeTag]);

  return { bootstrappedBrandingTag, isBootstrapping };
};

// Component that initializes UTM tags - must be inside UTMProvider
const UtmInitializer: FC = () => {
  const { initializeUtmTags } = useUTMContext();

  useEffect(() => {
    const syncUtmTagsFromUrl = () => {
      initializeUtmTags();
    };

    const handlePageShow = (event: Event) => {
      if ((event as PageTransitionEvent).persisted) {
        syncUtmTagsFromUrl();
      }
    };

    syncUtmTagsFromUrl();
    window.addEventListener("popstate", syncUtmTagsFromUrl);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("popstate", syncUtmTagsFromUrl);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [initializeUtmTags]);

  return null;
};

// Component that creates the app theme and provider hierarchy
const ThemedApp: FC = () => {
  const { isLoading } = useEnvironment();
  const { utmTags } = useUTMContext();
  const { bootstrappedBrandingTag, isBootstrapping } = useReferBrandingBootstrap(
    utmTags?.utm_theme,
  );
  useInvocaPhoneNumberTracking(PHONE_NUMBER);
  const activeBrandingTag = useActiveBrandingTag(utmTags?.utm_theme);
  const storedBrandingTag = getStoredBranding();
  const resolvedBrandingTag = isBootstrapping
    ? OPTUM_THEME_NAME
    : (utmTags?.utm_theme ?? storedBrandingTag ?? activeBrandingTag ?? bootstrappedBrandingTag);

  const activeThemeName = getThemeNameFromTag(resolvedBrandingTag);
  const activeThemeConfig = THEME_CONFIGS[activeThemeName];
  const defaultThemeConfig = THEME_CONFIGS[OPTUM_THEME_NAME];

  // Always merge default theme with active theme so default colors are always available.
  // Active theme colors override defaults if both define the same token.
  const themeConfig: ThemeConfig = {
    theme: {
      colors: {
        ...defaultThemeConfig.theme.colors,
        ...activeThemeConfig.theme.colors,
      },
    },
  };

  const theme = createTheme?.(OPTUM_THEME_NAME, themeConfig);

  const bootstrapLoadingOverlay = (
    <LoadingOverlay isLoading={true} ariaLoadingLabel="Loading partner branding..." />
  );

  if (isLoading || isBootstrapping) {
    if (AbyssProvider) {
      return <AbyssProvider theme={theme}>{bootstrapLoadingOverlay}</AbyssProvider>;
    }

    if (ThemeProvider) {
      return <ThemeProvider theme={theme}>{bootstrapLoadingOverlay}</ThemeProvider>;
    }

    return bootstrapLoadingOverlay;
  }

  const hasSplitProviders = Boolean(ThemeProvider || RouterProvider);

  let content: ReactNode;
  if (AbyssProvider) {
    content = (
      <AbyssProvider theme={theme} router={router}>
        {!router ? <FeatureGatedRoutes /> : null}
      </AbyssProvider>
    );
  } else if (hasSplitProviders) {
    const ActiveThemeProvider = ThemeProvider;
    const ActiveRouterProvider = RouterProvider;

    if (!ActiveThemeProvider || !ActiveRouterProvider) {
      content = <Routes />;
    } else {
      content = (
        <ActiveThemeProvider theme={theme}>
          <ActiveRouterProvider router={router}>
            {!router ? <FeatureGatedRoutes /> : null}
          </ActiveRouterProvider>
        </ActiveThemeProvider>
      );
    }
  } else {
    content = <FeatureGatedRoutes />;
  }

  return (
    <ApplicationProvider>
      <CanvasPrefillController />
      <FeatureFlagProvider>
        <InteractionGuardProvider>
          <TimerProvider>
            <ThemeContextProvider activeThemeName={activeThemeName}>
              <ComponentNameProvider>{content}</ComponentNameProvider>
            </ThemeContextProvider>
          </TimerProvider>
        </InteractionGuardProvider>
      </FeatureFlagProvider>
    </ApplicationProvider>
  );
};

export const browser = () => {
  return (
    <EnvironmentProvider>
      <CanvasProvider>
        <UTMProvider>
          <UtmInitializer />
          <ThemedApp />
        </UTMProvider>
      </CanvasProvider>
    </EnvironmentProvider>
  );
};
